import React, { useState } from 'react';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { Button } from '../ui/Button';
import { Settings, ChevronDown, ChevronUp } from 'lucide-react';

export function CookieConsent() {
  const { showBanner, acceptAll, rejectAll, acceptCustom } = useCookieConsent();
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [advertisingEnabled, setAdvertisingEnabled] = useState(false);

  if (!showBanner) {
    return null;
  }

  const handleSavePreferences = () => {
    acceptCustom({
      ad_Storage: advertisingEnabled ? 'granted' : 'denied',
      analytics_Storage: analyticsEnabled ? 'granted' : 'denied',
    });
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('cookieConsentTitle')}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              {t('cookieConsentDescription')}
            </p>

            {showDetails && (
              <div className="mt-4 space-y-3 text-sm text-gray-600">
                <div>
                  <p className="font-medium text-gray-900 mb-1">
                    {t('cookieConsentNecessary')}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-900 mb-1">
                    {t('cookieConsentAnalytics')}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-900 mb-1">
                    {t('cookieConsentAdvertising')}
                  </p>
                </div>
              </div>
            )}

            {showCustomize && (
              <div className="mt-4 space-y-3 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      {t('cookieConsentAnalytics')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('cookieConsentNecessary')}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={analyticsEnabled}
                      onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      {t('cookieConsentAdvertising')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Used for personalized ads
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={advertisingEnabled}
                      onChange={(e) => setAdvertisingEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {!showCustomize ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowDetails(!showDetails);
                    setShowCustomize(false);
                  }}
                  className="w-full sm:w-auto"
                >
                  {showDetails ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-1" />
                      Hide Details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-1" />
                      {t('cookieConsentLearnMore')}
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowCustomize(true);
                    setShowDetails(false);
                  }}
                  className="w-full sm:w-auto"
                >
                  <Settings className="w-4 h-4 mr-1" />
                  {t('cookieConsentCustomize')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={rejectAll}
                  className="w-full sm:w-auto"
                >
                  {t('cookieConsentRejectAll')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={acceptAll}
                  className="w-full sm:w-auto"
                >
                  {t('cookieConsentAcceptAll')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowCustomize(false);
                    setShowDetails(false);
                  }}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSavePreferences}
                  className="w-full sm:w-auto"
                >
                  {t('cookieConsentSavePreferences')}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

